import {
  getCurrentUser,
  type AuthEmployee,
  type AuthUser,
} from './authService';
import { getEmployeeDetails } from './employeeService';
import { getCurrentOrganization } from './organizationService';
import type { ProfileView } from '../types/profile';
import { normalizeApiError } from '../api/apiError';

function textOrNull(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  return null;
}

function nestedText(value: unknown, key: string): string | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return textOrNull((value as Record<string, unknown>)[key]);
}

function buildBaseProfile(user: AuthUser, roles: string[]): ProfileView {
  const userRecord = user as AuthUser & Record<string, unknown>;
  return {
    displayName: textOrNull(user.displayName) ?? textOrNull(user.username),
    employeeNumber: textOrNull(user.employeeNumber),
    email: textOrNull(user.email),
    mobile: textOrNull(user.phoneNumber),
    organizationName: textOrNull(userRecord.organizationName),
    department: textOrNull(userRecord.departmentName),
    designation: textOrNull(userRecord.designationName),
    manager: textOrNull(userRecord.reportingManagerName),
    joiningDate: textOrNull(userRecord.joiningDate),
    status: textOrNull(userRecord.statusName),
    roles,
  };
}

function enrichFromCurrentEmployee(
  profile: ProfileView,
  employee: AuthEmployee | null,
): void {
  if (!employee) return;
  const joinedName = [employee.firstName, employee.middleName, employee.lastName]
    .map(textOrNull)
    .filter(Boolean)
    .join(' ');

  profile.displayName =
    textOrNull(employee.displayName) ??
    textOrNull(employee.employeeName) ??
    textOrNull(employee.fullName) ??
    textOrNull(joinedName) ??
    profile.displayName;
  profile.employeeNumber =
    textOrNull(employee.employeeNumber) ?? profile.employeeNumber;
  profile.email =
    textOrNull(employee.officialEmail) ??
    textOrNull(employee.email) ??
    profile.email;
  profile.mobile =
    textOrNull(employee.mobileNumber) ??
    textOrNull(employee.phoneNumber) ??
    textOrNull(employee.mobile) ??
    profile.mobile;
  profile.organizationName =
    textOrNull(employee.organizationName) ?? profile.organizationName;
  profile.department =
    textOrNull(employee.departmentName) ??
    textOrNull(employee.department) ??
    nestedText(employee.department, 'departmentName') ??
    profile.department;
  profile.designation =
    textOrNull(employee.designationName) ??
    textOrNull(employee.designation) ??
    nestedText(employee.designation, 'designationName') ??
    profile.designation;
  profile.manager =
    textOrNull(employee.reportingManagerName) ??
    textOrNull(employee.managerName) ??
    textOrNull(employee.reportingManager) ??
    profile.manager;
  profile.joiningDate = textOrNull(employee.joiningDate) ?? profile.joiningDate;
  profile.status =
    textOrNull(employee.statusName) ??
    textOrNull(employee.employeeStatusName) ??
    textOrNull(employee.status) ??
    profile.status;
}

/**
 * Builds the My Profile view from /auth/me, optionally enriching with the
 * signed-in employee's detail record and GET /organization (SETTINGS_VIEW).
 * Internal IDs are never returned for display.
 */
export async function getMyProfile(options: {
  canViewEmployees: boolean;
  canViewOrganization: boolean;
}): Promise<ProfileView> {
  const current = await getCurrentUser();
  const profile = buildBaseProfile(current.user, current.roles);
  enrichFromCurrentEmployee(profile, current.employee);
  profile.organizationName =
    textOrNull(current.organization?.organizationName) ??
    profile.organizationName;
  const employeeId = Number(current.user.employeeId);

  // This is the signed-in employee's own record. Let the API enforce access;
  // EMPLOYEE_VIEW controls directory browsing and must not hide self-profile data.
  if (Number.isInteger(employeeId) && employeeId > 0) {
    try {
      const employee = await getEmployeeDetails(employeeId);
      profile.displayName = textOrNull(employee.displayName) ?? profile.displayName;
      profile.employeeNumber = textOrNull(employee.employeeNumber) ?? profile.employeeNumber;
      profile.email = textOrNull(employee.officialEmail) ?? profile.email;
      profile.mobile = textOrNull(employee.mobileNumber) ?? profile.mobile;
      profile.department = employee.department;
      profile.designation = employee.designation;
      profile.manager = employee.reportingManager;
      profile.joiningDate = employee.joiningDate;
      profile.status = employee.status;
    } catch (error) {
      // Enrichment is optional; keep /me fields when employee details fail.
      normalizeApiError(error);
    }
  }

  if (options.canViewOrganization) {
    try {
      const organization = await getCurrentOrganization();
      profile.organizationName = organization.organizationName;
    } catch (error) {
      normalizeApiError(error);
    }
  }

  return profile;
}
